import { observer } from "mobx-react-lite";
import { TextField, Input, Select, ListBox, ListBoxItem } from "@heroui/react";
import { contractorDetailStore } from "@/stores/ContractorDetailStore";
import type { LegalFormData } from "@/stores/ContractorDetailStore/legal-store/LegalDataSubStore";
import { numericOnly } from "@/pages/ContractorDetailPage/constants";
import { FormField } from "../form/FormField";
import { SuggestionDropdown } from "../suggestions/SuggestionDropdown";

const Step2Address: React.FC = observer(() => {
  const { legal } = contractorDetailStore;
  const { form, validationErrors: errors } = legal.form;

  const setField = (field: keyof LegalFormData, value: string) =>
    legal.form.setField(field, value);

  return (
    <div className="scd-drawer-form">
      <FormField
        label="Поиск адреса"
        tooltip="Поиск по базе ДаДата. Введите адрес для автозаполнения"
      >
        <div className="scd-drawer-search-wrap">
          <TextField fullWidth>
            <Input
              placeholder="Начните вводить адрес..."
              value={form.addressSearch}
              onChange={(e) => {
                setField("addressSearch", e.target.value);
                legal.dadata.suggestAddress(e.target.value);
                legal.dadata.setShowAddrSuggestions(true);
              }}
              onFocus={() => {
                if (legal.dadata.addrSuggestions.length > 0) {
                  legal.dadata.setShowAddrSuggestions(true);
                } else if (form.addressSearch.length >= 2) {
                  legal.dadata.suggestAddress(form.addressSearch);
                  legal.dadata.setShowAddrSuggestions(true);
                }
              }}
              onClick={() => {
                if (legal.dadata.addrSuggestions.length > 0) {
                  legal.dadata.setShowAddrSuggestions(true);
                }
              }}
              onBlur={() =>
                setTimeout(() => {
                  legal.dadata.setShowAddrSuggestions(false);
                }, 200)
              }
            />
          </TextField>
          <SuggestionDropdown
            suggestions={legal.dadata.addrSuggestions}
            visible={legal.dadata.showAddrSuggestions}
            onSelect={(s) => legal.applyAddrSuggestion(s)}
          />
        </div>
      </FormField>
      <FormField
        label="Страна регистрации"
        tooltip="Страна регистрации юридического лица"
      >
        <Select
          fullWidth
          selectedKey={form.legalCountry}
          onSelectionChange={(key) =>
            setField("legalCountry", (key as string) ?? "RUS")
          }
        >
          <Select.Trigger>
            <Select.Value />
            <Select.Indicator />
          </Select.Trigger>
          <Select.Popover>
            <ListBox>
              <ListBoxItem id="RUS">Россия</ListBoxItem>
            </ListBox>
          </Select.Popover>
        </Select>
      </FormField>
      <div className="scd-drawer-row">
        <FormField
          label="Город"
          error={errors.city}
          required
          tooltip="Город юридического адреса организации"
        >
          <TextField fullWidth>
            <Input
              placeholder="Москва"
              value={form.city}
              onChange={(e) => setField("city", e.target.value)}
            />
          </TextField>
        </FormField>
        <FormField
          label="Почтовый индекс"
          error={errors.postalCode}
          tooltip="Почтовый индекс юридического адреса, 6 цифр"
        >
          <TextField fullWidth>
            <Input
              placeholder="123456"
              value={form.postalCode}
              onChange={(e) =>
                setField("postalCode", e.target.value.replace(/\D/g, ""))
              }
              onKeyDown={numericOnly}
              maxLength={6}
            />
          </TextField>
        </FormField>
      </div>
      <FormField
        label="Улица"
        error={errors.street}
        required
        tooltip="Улица юридического адреса"
      >
        <TextField fullWidth>
          <Input
            placeholder="ул. Автозаводская"
            value={form.street}
            onChange={(e) => setField("street", e.target.value)}
          />
        </TextField>
      </FormField>
      <div className="scd-drawer-row">
        <FormField
          label="Дом / строение"
          error={errors.building}
          required
          tooltip="Номер дома, строения, корпуса"
        >
          <TextField fullWidth>
            <Input
              placeholder="д. 23, стр. 1"
              value={form.building}
              onChange={(e) => setField("building", e.target.value)}
            />
          </TextField>
        </FormField>
        <FormField
          label="Офис / помещение"
          error={errors.office}
          tooltip="Номер офиса или помещения (опционально)"
        >
          <TextField fullWidth>
            <Input
              placeholder="оф. 1"
              value={form.office}
              onChange={(e) => setField("office", e.target.value)}
            />
          </TextField>
        </FormField>
      </div>
    </div>
  );
});

Step2Address.displayName = "Step2Address";
export default Step2Address;
